import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AdminRouteContainer } from "components/general/AdminRouteContainer";
import { ModalHandler } from "components/general/ModalHandler";
import {
  Row,
  OverlayTrigger,
  Tooltip,
  Button,
  Form,
  Col,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import {
  faExclamationTriangle,
  faCheckCircle,
  faCircleInfo,
  faTimesCircle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { ICancelContinueModal, IConfirmModal } from "interfaces/IModal";
import { useEffect, useState } from "react";
import {
  createAuthenticatedAxiosInstance,
  patchSize,
  showSize,
} from "api/auth";
import { useAuthStore } from "../../zustand/loginStore";
import { isSuccessStatusCode } from "helpers/httpResponse";
import { Size } from "interfaces/ISize";

/**
 * The size details component
 */
export const SizeDetails = () => {
  const location = useLocation();
  const { id, status, name, description } = location.state || {};
  const [size, setSize] = useState<Size>();
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const modalStatusMessage = !!size?.status
    ? "Esta talla se desactivará."
    : "Esta talla se activará.";
  const initialModal = {
    show: false,
    onHide: () => {},
    onConfirm: () => {},
    title: "Atención",
    message: "",
    icon: faExclamationTriangle,
    iconColor: "text-danger",
  };
  const [modal, setModal] = useState<IConfirmModal | ICancelContinueModal>(
    initialModal
  );
  const navigate = useNavigate();

  /**
   * handles the back button click and returns to the previous route
   */
  const onEdit = (size: Size) =>
    navigate("/inventories/sizes/edit", { state: size });

  /**
   * Hides the modal and set the default initial modal
   */
  const onHideModal = () => setModal(initialModal);

  /**
   * on patch size modal that makes an api call to patch the current size
   */
  const onPatchSize = async () => {
    if (!size) return;
    const sizeBooleanStatus = !!size.status;

    const { data, statusCode } = await patchSize(
      axiosInstance,
      id,
      sizeBooleanStatus
    );

    if (!isSuccessStatusCode(statusCode)) {
      // Manejar error específico cuando no se puede desactivar por productos con stock
      if (
        statusCode === 400 &&
        (data as any)?.error === "No se puede desactivar la talla"
      ) {
        const errorData = data as any;
        const totalProducts = errorData.total_products || 0;

        // Procesar la lista de productos con stock
        let productList = "";
        if (
          errorData.products_with_stock &&
          Array.isArray(errorData.products_with_stock)
        ) {
          const productDetails = errorData.products_with_stock.map(
            (product: any) =>
              `• ${product.product_name} (${product.color}) - Stock: ${product.stock}`
          );
          productList = productDetails.join("\n");
        }

        const specificErrorMessage = `No se puede desactivar la talla.\n\nExisten ${totalProducts} producto(s) con esta talla que tienen stock disponible:\n\n${productList}\n\nPor favor, agota el stock de estos productos antes de desactivar la talla.`;

        setModal({
          title: "Error",
          message: specificErrorMessage,
          icon: faTimesCircle,
          iconColor: "text-danger",
          onHide: onHideModal,
          show: true,
        });
      } else {
        setModal({
          title: "Error",
          message: "Error al conectar con el servidor. Inténtalo más tarde.",
          icon: faTimesCircle,
          iconColor: "text-danger",
          onHide: onHideModal,
          show: true,
        });
      }
      return;
    }

    const successModal: IConfirmModal = {
      show: true,
      title: "Confirmación",
      message: data?.message ?? "",
      icon: faCheckCircle,
      iconColor: "text-success",
      onHide: onHideModal,
    };

    setModal(successModal);
    setSize({ ...size, status: sizeBooleanStatus ? 0 : 1 });
  };

  /**
   * Opens the status related modal
   */
  const onStatusClick = () => {
    setModal({
      ...modal,
      message: `${modalStatusMessage}\n¿Estás seguro de continuar?`,
      show: true,
      onConfirm: onPatchSize,
      onHide: onHideModal,
    });
  };

  /**
   * Fetch the size information
   */
  const fetchSize = async () => {
    const { data, statusCode } = await showSize(axiosInstance, id);
    if (!isSuccessStatusCode(statusCode)) {
      return;
    }
    setSize(data);
  };

  /**
   * fetch the size details are required on render
   */
  useEffect(() => {
    fetchSize();
  }, []);

  return (
    <AdminRouteContainer
      customClass="align-items-stretch"
      customMaxWidth={1000}
      backRoute="/inventories/sizes"
    >
      <>
        {size && (
          <>
            <Row className="justify-content-center text-center">
              <img
                src={logo}
                alt="Logo de la Boutique"
                className="mb-4"
                style={{ width: "215px" }}
              />
              <h2 className="fw-bold">Detalle talla</h2>
            </Row>
            <Row className="d-flex flex-nowrap align-items-center mb-4 mt-5">
              <Col xs={6}>
                <Form.Label className="fw-bold">No.</Form.Label>
                <div className="d-flex align-items-center">
                  <span style={{ fontSize: "24px", color: "#747775" }}>
                    {id}
                  </span>
                  <OverlayTrigger
                    trigger={["hover", "focus"]}
                    placement="right"
                    overlay={
                      <Tooltip className="custom-tooltip">
                        Número identificador de la talla
                      </Tooltip>
                    }
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="ms-2" />
                  </OverlayTrigger>
                </div>
              </Col>
              <div className="d-flex align-items-start flex-shrink-1 justify-content-end">
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip className="custom-tooltip">
                      Estado actual de la talla
                    </Tooltip>
                  }
                >
                  <span
                    className={`status-badge ${
                      size?.status ? "active" : "inactive"
                    }`}
                  >
                    {size?.status ? "Activo" : "Inactivo"}
                  </span>
                </OverlayTrigger>
              </div>
            </Row>
            <Row className="align-items-center mb-4 mt-5">
              <Form>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    Nombre de la talla:
                  </Form.Label>
                  <Form.Control
                    type="text"
                    onChange={() => {}}
                    value={size.name}
                    readOnly={true}
                    placeholder="Nombre de la talla "
                  />
                </Form.Group>
                <Form.Group className="mb-4 text-center justify-items-center">
                  <Form.Label className="fw-bold">
                    Distintivo
                    <OverlayTrigger
                      placement="right"
                      overlay={
                        <Tooltip className="custom-tooltip">
                          EL valor distintivo es el que verá el cliente al
                          momento de hacer su compra.
                          <br />
                          Ej. S, M, L o CH, M, G.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faCircleInfo} className="ms-2" />
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    style={{ width: "20%", justifySelf: "center" }}
                    type="text"
                    onChange={() => {}}
                    value={size.description}
                    readOnly={true}
                    placeholder="Distintivo "
                  />
                </Form.Group>
              </Form>
            </Row>
            <Row className="d-flex justify-content-between">
              {" "}
              <Button
                variant="secondary"
                type="button"
                onClick={onStatusClick}
                className=" fw-bold align-item-center text-center me-2"
                style={{ width: "fit-content" }}
              >
                {size.status ? "Desactivar" : "Activar"}
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={() => onEdit(size)}
                className=" fw-bold align-item-center text-center ms-2"
                style={{ width: "fit-content" }}
              >
                Editar
              </Button>
            </Row>
          </>
        )}
        <ModalHandler modal={modal} />
      </>
    </AdminRouteContainer>
  );
};
